import { useEffect, useState } from 'react';
import {
  Loader2, Check, Eye, EyeOff,
  User as UserIcon, Building2, CreditCard, Plug, Zap,
  Calendar as CalendarIcon, Inbox, Flag, SlidersHorizontal,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { useSuperAdminAuth , saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { formatDate } from '@/lib/dateFormat';
import { LeadSourcesSection } from './SuperAdminLeadSourcesPage';
import { LeadStatusesSection } from './SuperAdminLeadStatusesPage';
import { IntegrationsTab } from '@/pages/SettingsPage';
import { SelectField } from '@/components/ui/SelectField';

type Tab = 'profile' | 'platform' | 'billing' | 'integrations' | 'financial-year' | 'lead-sources' | 'lead-statuses';

// Tab definitions with icons — mirrors the vendor /settings layout for
// visual parity. The Integrations + Automations tabs are reused via the
// shared parameterized components from pages/SettingsPage; everything
// else is super-admin-specific. Email provider config lives under the
// Integrations tab (Email Provider card), so there is no separate
// Email Settings tab here.
const TABS: { key: Tab; label: string; icon: typeof UserIcon }[] = [
  { key: 'profile',        label: 'Profile',          icon: UserIcon },
  { key: 'platform',       label: 'Platform Settings', icon: Building2 },
  { key: 'billing',        label: 'Billing Settings', icon: CreditCard },
  { key: 'financial-year', label: 'Financial Year',   icon: CalendarIcon },
  { key: 'lead-sources',   label: 'Lead Sources',     icon: Inbox },
  { key: 'lead-statuses',  label: 'Lead Statuses',    icon: Flag },
  { key: 'integrations',   label: 'Integrations',     icon: Plug },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'SGD', 'AED'];
const TIMEZONES = ['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'];

// ── Shared input styles ────────────────────────────────────────────────────────
const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-[#94A3B8] transition-all';
const labelCls = 'block text-xs font-medium text-[#64748B] mb-1.5';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

// ── Profile Tab ────────────────────────────────────────────────────────────────
function ProfileTab() {
  const { admin } = useSuperAdminAuth();
  const [name, setName] = useState(admin?.name || '');
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  const saveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await saApi.patch(`/super-admin/users/${admin?.id}`, { name });
      toast('success', 'Profile updated');
    } catch { toast('error', 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast('error', 'New passwords do not match'); return; }
    if (pwd.next.length < 6) { toast('error', 'Password must be at least 6 characters'); return; }
    setPwdSaving(true);
    try {
      await saApi.post(`/super-admin/users/${admin?.id}/change-password`, { newPassword: pwd.next });
      setPwd({ current: '', next: '', confirm: '' });
      toast('success', 'Password changed successfully');
    } catch { toast('error', 'Failed to change password'); }
    finally { setPwdSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#1E293B]">Personal Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full Name">
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
          </Field>
          <Field label="Email">
            <input className={`${inputCls} bg-gray-50 text-[#94A3B8]`} value={admin?.email || ''} disabled />
          </Field>
        </div>
        <div className="flex justify-end">
          <button onClick={saveName} disabled={saving || !name.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#1E293B]">Change Password</h3>
        <form onSubmit={changePassword} className="space-y-4">
          <Field label="Current Password">
            <input type="password" className={inputCls} value={pwd.current}
              onChange={e => setPwd(p => ({ ...p, current: e.target.value }))} placeholder="Current password" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="New Password">
              <input type="password" className={inputCls} value={pwd.next}
                onChange={e => setPwd(p => ({ ...p, next: e.target.value }))} placeholder="Min 6 characters" />
            </Field>
            <Field label="Confirm New Password">
              <input type="password" className={inputCls} value={pwd.confirm}
                onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} placeholder="Confirm password" />
            </Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={pwdSaving || !pwd.current || !pwd.next || !pwd.confirm}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors">
              {pwdSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Platform Settings Tab ──────────────────────────────────────────────────────
function PlatformTab() {
  const [form, setForm] = useState({
    platformName: 'BrixLead',
    supportEmail: 'support@brixlead.com',
    supportPhone: '',
    currency: 'USD',
    timezone: 'UTC',
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    saApi.get('/super-admin/settings').then(({ data }) => {
      setForm({
        platformName: data.platformName || 'BrixLead',
        supportEmail: data.supportEmail || 'support@brixlead.com',
        supportPhone: data.supportPhone || '',
        currency: data.currency || 'USD',
        timezone: data.timezone || 'UTC',
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saApi.patch('/super-admin/settings', form);
      toast('success', 'Platform settings saved');
    } catch { toast('error', 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
      <h3 className="text-base font-semibold text-[#1E293B]">Platform Settings</h3>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Platform Name">
          <input className={inputCls} value={form.platformName}
            onChange={e => setForm(p => ({ ...p, platformName: e.target.value }))} />
        </Field>
        <Field label="Support Email">
          <input type="email" className={inputCls} value={form.supportEmail}
            onChange={e => setForm(p => ({ ...p, supportEmail: e.target.value }))} />
        </Field>
        <Field label="Support Phone">
          <input className={inputCls} placeholder="+1 (555) 000-0000" value={form.supportPhone}
            onChange={e => setForm(p => ({ ...p, supportPhone: e.target.value }))} />
        </Field>
        <Field label="Default Currency">
          <SelectField className={inputCls} value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </SelectField>
        </Field>
        <Field label="Default Timezone">
          <SelectField className={inputCls} value={form.timezone} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}>
            {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
          </SelectField>
        </Field>
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}

// ── AI Models (platform-wide) ───────────────────────────────────────────────────
// Which model each provider uses for the AI Assistant. Tenants choose the
// provider + supply their key (Settings → AI); the super-admin sets the model
// here, applied to all tenants. Stored in PlatformSetting.integrations.ai.
function AiModelsCard() {
  const [models, setModels] = useState({ anthropicModel: '', openaiModel: '', geminiModel: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    saApi.get('/super-admin/settings/integrations').then(({ data }) => {
      const ai = data?.ai ?? {};
      setModels({
        anthropicModel: ai.anthropicModel || 'claude-sonnet-4-20250514',
        openaiModel: ai.openaiModel || 'gpt-4o',
        geminiModel: ai.geminiModel || 'gemini-2.0-flash',
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saApi.patch('/super-admin/settings/integrations', { ai: models });
      toast('success', 'AI models saved');
    } catch { toast('error', 'Failed to save AI models'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-[#1E293B]">AI Models</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Model each AI provider uses for the Assistant + drafts. Applies to all tenants — tenants pick the
          provider and add their own key under Settings → AI Assistant; you set the model here.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Claude (Anthropic) model">
          <input className={inputCls} value={models.anthropicModel}
            onChange={e => setModels(p => ({ ...p, anthropicModel: e.target.value }))} placeholder="claude-sonnet-4-20250514" />
        </Field>
        <Field label="ChatGPT (OpenAI) model">
          <input className={inputCls} value={models.openaiModel}
            onChange={e => setModels(p => ({ ...p, openaiModel: e.target.value }))} placeholder="gpt-4o" />
        </Field>
        <Field label="Gemini (Google) model">
          <input className={inputCls} value={models.geminiModel}
            onChange={e => setModels(p => ({ ...p, geminiModel: e.target.value }))} placeholder="gemini-2.0-flash" />
        </Field>
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save Models
        </button>
      </div>
    </div>
  );
}

// ── Revealable secret input ────────────────────────────────────────────────────
// Reads a masked placeholder from the server; on the eye-toggle it fetches
// the decrypted value from the /reveal endpoint (only for sensitive fields).
function RevealSecretInput({ value, onChange, field, placeholder }: {
  value: string; onChange: (v: string) => void; field: string; placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMasked = value.startsWith('************');

  const toggle = async () => {
    if (!show && isMasked && value) {
      setBusy(true);
      try {
        const { data } = await saApi.get(`/super-admin/settings/integrations/payment/${field}/reveal`);
        onChange(data?.value ?? '');
      } catch { toast('error', 'Failed to reveal value'); }
      finally { setBusy(false); }
    }
    setShow(s => !s);
  };

  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        className={`${inputCls} pr-10`}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || '••••••••'}
      />
      <button type="button" onClick={toggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

// ── Billing Settings Tab ───────────────────────────────────────────────────────
// Payment gateway credentials live in the PlatformSetting `payment` section
// (encrypted at rest), NOT in Railway env — so the platform admin can rotate
// keys or switch provider without a redeploy. Secrets come back masked and
// are revealed on demand via the eye toggle.
const PAY_CURRENCIES = ['usd', 'eur', 'gbp', 'inr', 'aud', 'cad', 'sgd', 'aed'];
const WEBHOOK_URL = `${(import.meta.env.VITE_API_URL || '').replace(/\/$/, '')}/api/webhooks/stripe`;

function BillingTab() {
  const [form, setForm] = useState({
    activeProvider: '',
    stripeSecretKey: '', stripePublishableKey: '', stripeWebhookSecret: '',
    razorpayKeyId: '', razorpayKeySecret: '',
    currency: 'usd', usdToInr: '83', annualDiscountPercent: '0',
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    saApi.get('/super-admin/settings/integrations').then(({ data }) => {
      const p = (data?.payment ?? {}) as Record<string, any>;
      setForm({
        activeProvider: p.activeProvider || '',
        stripeSecretKey: p.stripeSecretKey || '',
        stripePublishableKey: p.stripePublishableKey || '',
        stripeWebhookSecret: p.stripeWebhookSecret || '',
        razorpayKeyId: p.razorpayKeyId || '',
        razorpayKeySecret: p.razorpayKeySecret || '',
        currency: p.currency || 'usd',
        usdToInr: p.usdToInr != null ? String(p.usdToInr) : '83',
        annualDiscountPercent: p.annualDiscountPercent != null ? String(p.annualDiscountPercent) : '0',
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saApi.patch('/super-admin/settings/integrations', {
        payment: {
          activeProvider: form.activeProvider,
          stripeSecretKey: form.stripeSecretKey,
          stripePublishableKey: form.stripePublishableKey,
          stripeWebhookSecret: form.stripeWebhookSecret,
          razorpayKeyId: form.razorpayKeyId,
          razorpayKeySecret: form.razorpayKeySecret,
          currency: form.currency,
          usdToInr: Number(form.usdToInr) || 83,
          annualDiscountPercent: Math.min(90, Math.max(0, Number(form.annualDiscountPercent) || 0)),
        },
      });
      toast('success', 'Payment settings saved');
    } catch { toast('error', 'Failed to save payment settings'); }
    finally { setSaving(false); }
  };

  if (!loaded) {
    return <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 mx-auto" /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Provider + currency */}
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-[#1E293B]">Payment Gateways</h3>
          <p className="text-xs text-[#64748B] mt-1">Managed here — no keys are stored on the server env. Rotate or switch provider anytime; changes apply on the next payment.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Active Provider">
            <SelectField className={inputCls} value={form.activeProvider} onChange={e => set('activeProvider', e.target.value)}>
              <option value="">Both (vendor chooses)</option>
              <option value="stripe">Stripe only</option>
              <option value="razorpay">Razorpay only</option>
            </SelectField>
          </Field>
          <Field label="Currency">
            <SelectField className={inputCls} value={form.currency} onChange={e => set('currency', e.target.value)}>
              {PAY_CURRENCIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </SelectField>
          </Field>
          <Field label="USD → INR rate (Razorpay)">
            <input className={inputCls} value={form.usdToInr} onChange={e => set('usdToInr', e.target.value)} placeholder="83" />
          </Field>
          <Field label="Annual discount (%)">
            <input className={inputCls} value={form.annualDiscountPercent} onChange={e => set('annualDiscountPercent', e.target.value)} placeholder="20" />
          </Field>
        </div>
      </div>

      {/* Stripe */}
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#1E293B]">Stripe</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Secret Key">
            <RevealSecretInput value={form.stripeSecretKey} onChange={v => set('stripeSecretKey', v)} field="stripeSecretKey" placeholder="sk_live_..." />
          </Field>
          <Field label="Publishable Key">
            <input className={inputCls} value={form.stripePublishableKey} onChange={e => set('stripePublishableKey', e.target.value)} placeholder="pk_live_..." />
          </Field>
          <Field label="Webhook Signing Secret">
            <RevealSecretInput value={form.stripeWebhookSecret} onChange={v => set('stripeWebhookSecret', v)} field="stripeWebhookSecret" placeholder="whsec_..." />
          </Field>
          <div className="flex flex-col justify-end">
            <label className={labelCls}>Webhook URL (register in Stripe dashboard)</label>
            <code className="block h-10 leading-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-[#475569] truncate">{WEBHOOK_URL || '/api/webhooks/stripe'}</code>
          </div>
        </div>
      </div>

      {/* Razorpay */}
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#1E293B]">Razorpay</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Key ID">
            <input className={inputCls} value={form.razorpayKeyId} onChange={e => set('razorpayKeyId', e.target.value)} placeholder="rzp_live_..." />
          </Field>
          <Field label="Key Secret">
            <RevealSecretInput value={form.razorpayKeySecret} onChange={v => set('razorpayKeySecret', v)} field="razorpayKeySecret" placeholder="••••••••" />
          </Field>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}

// ── Settings Page ──────────────────────────────────────────────────────────────
//
// Visual parity with the vendor /settings page: same gradient hero card,
// same icon-prefixed sidebar, same active-tab gradient pill with the
// purple chevron landing on the content panel, same outer rounded shell
// with a single border (no double-border issue) and the inner Card
// chrome stripped via [&_.bg-card]:!border-0 etc.
//
// Functional differences from vendor side: no Tenant Workspace tab, no
// Brand Settings tab, no Lead Columns / User Fields / Custom Fields
// (those are tenant-scoped); but adds Platform Settings + Billing
// Settings (super-admin operations). Email provider config is unified
// under the Integrations tab.

export function SuperAdminSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('profile');

  return (
    <SuperAdminLayout title="Settings">
      <div className="space-y-6">
        {/* Page header — gradient brand strip with icon tile, matching the
            vendor /settings hero card. */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-slate-100 p-6 sm:p-7 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-lg shadow-indigo-200/50 flex-shrink-0">
            <SlidersHorizontal className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Settings</h1>
            <p className="text-sm text-slate-600 mt-1">Manage the platform configuration, integrations, and automations.</p>
          </div>
        </div>

        {/* Unified shell — sidebar + content share one outer border, active
            tab punches through the divider via -mr-px + chevron. Inner
            Card borders stripped via the descendant arbitrary variant on
            the content area. */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col md:flex-row md:gap-0">
          {/* Sidebar */}
          <div className="md:w-56 md:flex-shrink-0 md:border-r md:border-slate-200 md:bg-slate-50/40 p-3 md:p-4">
            <nav className="flex md:flex-col gap-1 md:gap-1 md:sticky md:top-4 overflow-x-auto md:overflow-visible pb-2 md:pb-0 scrollbar-thin">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`group whitespace-nowrap md:w-full md:text-left px-3 py-2.5 text-sm font-medium transition-all flex-shrink-0 flex items-center gap-2.5 ${
                      active
                        ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold shadow-md shadow-indigo-200/60 rounded-xl md:rounded-r-none md:-mr-px md:relative md:z-10 md:after:content-[""] md:after:absolute md:after:left-full md:after:top-1/2 md:after:-translate-y-1/2 md:after:w-0 md:after:h-0 md:after:border-y-[7px] md:after:border-y-transparent md:after:border-l-[7px] md:after:border-l-[#8B5CF6]'
                        : 'rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${
                        active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'
                      }`}
                    />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right content — strip nested Card chrome so the only visible
              border is the outer shell's. */}
          <div className="flex-1 min-w-0 p-4 sm:p-6 [&_.bg-card]:!border-0 [&_.bg-card]:!shadow-none [&_.bg-card]:!bg-transparent [&_.bg-card]:!rounded-none">
            {activeTab === 'profile' && <ProfileTab />}
            {activeTab === 'platform' && (
              <div className="space-y-5">
                <PlatformTab />
                <AiModelsCard />
              </div>
            )}
            {activeTab === 'billing' && <BillingTab />}
            {activeTab === 'integrations' && (
              <IntegrationsTab
                apiClient={saApi}
                endpoint="/super-admin/settings/integrations"
                mode="platform"
                whatsappAccountsBase="/super-admin/whatsapp"
              />
            )}
            {activeTab === 'financial-year' && <FinancialYearTab />}
            {activeTab === 'lead-sources' && <LeadSourcesSection />}
            {activeTab === 'lead-statuses' && <LeadStatusesSection />}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}

// ── Financial Year Tab ────────────────────────────────────────────────────────

function FinancialYearTab() {
  const [years, setYears] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    saApi.get('/super-admin/financial-years').then(({ data }) => setYears(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Financial Years</h3>
        <p className="text-sm text-slate-500">View financial years across all organizations (vendors manage their own)</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 mx-auto" /></div>
        ) : years.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No financial years configured by vendors yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b-2 border-slate-200">
                {['Organization', 'Financial Year', 'Start', 'End', 'Status', 'Created By'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {years.map((fy: any) => (
                <tr key={fy.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 font-medium">{fy.tenant?.name}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{fy.name}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(fy.startDate)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(fy.endDate)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${fy.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {fy.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fy.createdBy?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

